import { useMemo } from 'react';

import { Add } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { useMatch } from 'react-router-dom';

import { competitorsFormFieldStyles } from 'components/FormFields/CompetitorsFormField/CompetitorsFormField.styles';
import strings, { url } from 'constants/index';
import { rowsPerPageOptions } from 'constants/pagination';
import { ColumnIds } from 'enums';
import {
  urlByCompetitorTypeMapping,
  urlByCompetitorTypeMappingRouting,
} from 'features/competitors/Competitors.utils';
import { CompetitorDouble } from 'features/competitors/doubles/Doubles.types';
import { PersonData } from 'features/persons';
import { usePaginatedRequest } from 'hooks/api';
import { useCompetitorsType } from 'hooks/context';
import { ActionsColumn, getIdFormatter, useDateOfBirthColumn } from 'utils/table.utils';

import { CompetitorTeamData } from '../../features/competitors/teams';
import CompetitorFilters from './CompetitorFilters';
import { columnsByCompetitorTypeMapping, roundColumns } from './CompetitorsAddTable.columns';
import { Table, TableColumn } from 'components/Table';

interface CompetitorsAddTableProps {
  onAdd: (competitor: PersonData | CompetitorTeamData | CompetitorDouble) => void;
  staticFilters: Array<PaginationFilter>;
  competitors: Competitor[];
  genderType?: string;
  seasonId?: number;
}

const defaultStaticFilters: Array<PaginationFilter> = [
  {
    columnId: 'competitorStatusType',
    value: 'Active',
  },
];

const personStaticFilters: Array<PaginationFilter> = [
  {
    columnId: 'isActive',
    value: 'true',
  },
];

export const CompetitorsAddTable = ({
  onAdd,
  staticFilters,
  seasonId,
  competitors,
  genderType,
}: CompetitorsAddTableProps) => {
  const [competitorsType] = useCompetitorsType();
  const isRound = !!useMatch(`${url.base_rounds}/:roundId`);
  const { data, setFilters, setPageSize, setPage, isLoading } = usePaginatedRequest<
    PersonData | CompetitorTeamData | CompetitorDouble | Competitor
  >({
    endpoint: isRound
      ? `${url.seasons}/${seasonId}/competitors`
      : urlByCompetitorTypeMapping[competitorsType],
    disableSportId: true,
    initialParams: {
      pageSize: 5,
    },
    staticFilters: staticFilters
      ? staticFilters.concat(
          competitorsType === strings.person ? personStaticFilters : defaultStaticFilters,
        )
      : defaultStaticFilters,
  });

  const DateOfBirthColumn = useDateOfBirthColumn(false, false);
  const idFormatter = useMemo(
    () => getIdFormatter(`${urlByCompetitorTypeMappingRouting[competitorsType]}`),
    [competitorsType],
  );

  const getCompetitorIdUrlForRound = useMemo(
    () => (value: number, competitor: any) =>
      getIdFormatter(urlByCompetitorTypeMappingRouting[competitor.competitorType])(
        value,
        competitor,
      ),
    [],
  );
  const columns = useMemo<Array<TableColumn>>(() => {
    const cols: Array<TableColumn> = [
      {
        id: ColumnIds.Id,
        label: strings.id,
        formatter: isRound ? getCompetitorIdUrlForRound : idFormatter,
      },
      ...(isRound ? roundColumns : columnsByCompetitorTypeMapping[competitorsType]),
      {
        ...ActionsColumn,
        formatter: (_, competitor: PersonData | CompetitorTeamData | CompetitorDouble) => (
          <IconButton
            disabled={competitors.some(x => x.id === competitor.id)}
            title="add"
            onClick={() => onAdd(competitor)}
            size="large"
          >
            <Add />
          </IconButton>
        ),
      },
    ];

    if (
      (competitorsType === strings.person ||
        competitorsType === strings.player ||
        competitorsType === strings.double) &&
      !isRound
    ) {
      cols.splice(
        2,
        0,
        competitorsType === strings.player
          ? { ...DateOfBirthColumn, id: `person.${ColumnIds.DateOfBirth}` }
          : DateOfBirthColumn,
      );
    }

    return cols;
  }, [
    DateOfBirthColumn,
    competitors,
    competitorsType,
    getCompetitorIdUrlForRound,
    idFormatter,
    isRound,
    onAdd,
  ]);

  const effectivePageSize = data?.pageSize ?? 5;
  const effectivePage = data?.page ?? 1;

  return (
    <>
      <CompetitorFilters onChange={setFilters} genderType={genderType} />
      <Box sx={competitorsFormFieldStyles.listContainer}>
        <Table
          localStorageKey="competitorsAddTable"
          columns={columns}
          loading={isLoading}
          onChangePage={(page: number) => setPage(page + 1)}
          onChangeRowsPerPage={setPageSize}
          page={Math.max(0, effectivePage - 1)}
          rows={data.items ?? []}
          rowsCount={data.totalItems ?? -1}
          rowsPerPage={effectivePageSize}
          rowsPerPageOptions={[5, ...rowsPerPageOptions]}
        />
      </Box>
    </>
  );
};
